import { api } from './api';
import type {
  ISellerContractAcceptPayload,
  ISellerContractCurrent,
  ISellerContractStatus,
} from '@/@types/SellerContract';

const BASE = '/v1/seller-contract';
const USERS_BASE = '/v1/users';

function extractPayload<T>(body: { data?: T } & T): T {
  if (
    body &&
    typeof body === 'object' &&
    'data' in body &&
    body.data !== undefined
  ) {
    return body.data as T;
  }
  return body as T;
}

async function getCurrent(): Promise<ISellerContractCurrent> {
  const { data } = await api.get<
    { data?: ISellerContractCurrent } & ISellerContractCurrent
  >(`${BASE}/current`);
  return extractPayload(data);
}

async function getStatus(): Promise<ISellerContractStatus> {
  const { data } = await api.get<
    { data?: ISellerContractStatus } & ISellerContractStatus
  >(`${BASE}/status`);
  return extractPayload(data);
}

function getSourcePath(contractVersion: string, locale: string): string {
  const params = new URLSearchParams({ locale });
  return `${BASE}/${encodeURIComponent(contractVersion)}/source?${params.toString()}`;
}

function getDownloadPath(contractVersion: string, inline?: boolean): string {
  const path = `${BASE}/${encodeURIComponent(contractVersion)}/download`;
  if (inline) {
    return `${path}?inline=1`;
  }
  return path;
}

/** Monta path relativo com locale (fallback quando `/current` não traz URLs absolutas). */
function buildDownloadPathWithLocale(
  contractVersion: string,
  locale: string,
  inline?: boolean,
): string {
  const params = new URLSearchParams({ locale });
  if (inline) params.set('inline', '1');
  return `${BASE}/${encodeURIComponent(contractVersion)}/download?${params.toString()}`;
}

function pickSignedContractRequestUrl(
  current: ISellerContractCurrent,
  locale: 'pt' | 'en',
  inline: boolean,
): string | null {
  const trim = (u?: string | null) => (u ?? '').trim() || null;
  if (inline) {
    if (locale === 'en') {
      return (
        trim(current.signed_pdf_url_en) ?? trim(current.signed_pdf_url) ?? null
      );
    }
    return (
      trim(current.signed_pdf_url_pt) ?? trim(current.signed_pdf_url) ?? null
    );
  }
  if (locale === 'en') {
    return trim(current.download_url_en) ?? trim(current.download_url) ?? null;
  }
  return trim(current.download_url_pt) ?? trim(current.download_url) ?? null;
}

/**
 * Baixa o PDF assinado usando as URLs de `/seller-contract/current` quando existirem;
 * senão usa `/download?locale=…`.
 */
async function fetchSignedContractBlob(
  current: ISellerContractCurrent,
  options: { kind: 'contract' | 'receipt'; locale: 'pt' | 'en' },
): Promise<Blob> {
  const inline = options.kind === 'receipt';
  const absoluteOrRelative = pickSignedContractRequestUrl(
    current,
    options.locale,
    inline,
  );
  if (absoluteOrRelative) {
    const { data } = await api.get(absoluteOrRelative, {
      responseType: 'blob',
    });
    return data;
  }
  const path = buildDownloadPathWithLocale(
    current.contract_version,
    options.locale,
    inline,
  );
  const { data } = await api.get(path, { responseType: 'blob' });
  return data;
}

/** PDF do contrato */
async function fetchSourceBlob(
  contractVersion: string,
  locale: string,
): Promise<Blob> {
  const { data } = await api.get(getSourcePath(contractVersion, locale), {
    responseType: 'blob',
  });
  return data;
}

async function fetchSignedDownloadBlob(contractVersion: string): Promise<Blob> {
  const { data } = await api.get(getDownloadPath(contractVersion), {
    responseType: 'blob',
  });
  return data;
}

async function fetchSignedPdfInlineBlob(
  contractVersion: string,
): Promise<Blob> {
  const { data } = await api.get(getDownloadPath(contractVersion, true), {
    responseType: 'blob',
  });
  return data;
}

async function accept(payload?: ISellerContractAcceptPayload): Promise<void> {
  await api.post(`${BASE}/accept`, payload ?? {});
}

async function reissueSignedPdfBlob(
  contractVersion: string,
  locale: string,
): Promise<Blob> {
  const { data } = await api.post(
    `${BASE}/${encodeURIComponent(contractVersion)}/reissue?locale=${locale}`,
    {},
    { responseType: 'blob' },
  );
  return data;
}

async function fetchSignedPdfForUserBlob(userId: string): Promise<Blob> {
  const safeUserId = encodeURIComponent(userId);
  const { data } = await api.post(
    `${USERS_BASE}/${safeUserId}/seller-contract-signed-pdf`,
    {},
    { responseType: 'blob' },
  );
  return data;
}

export const SellerContractService = {
  getCurrent,
  getStatus,
  getSourcePath,
  getDownloadPath,
  fetchSourceBlob,
  fetchSignedDownloadBlob,
  fetchSignedContractBlob,
  fetchSignedPdfInlineBlob,
  accept,
  reissueSignedPdfBlob,
  fetchSignedPdfForUserBlob,
};
